import { z } from "zod";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import {
  effectivenessList,
  systemInformAreaList,
  systemInformKPIList,
} from "../../../utils/types";
import { formatOptions } from "../../../utils/helpers";
import axios from "axios";
import { useState, useEffect } from "react";
import { useRequestStore } from "../../../data/stores/loggerStore";

const formSchema = z.object({
  erpModule: z.string().min(1, "Data system is required"),
  systemInformKPI: z.array(z.string()).min(1, "Required"),
  systemInformArea: z.array(z.string()).min(1, "Required"),
  systemReceiveData: z.string().min(1, "Required"),
  systemCommunicationEffectiveness: z.string().min(1, "Required"),
});

type FormData = z.infer<typeof formSchema>;

const ExternalDataSharingEdit = ({ data, setIsEditing }) => {
  const [customKPI, setCustomKPI] = useState<string>("");
  const [customKPIs, setCustomKPIs] = useState<string[]>([]);
  const [hasOtherKPI, setHasOtherKPI] = useState<boolean>(false);
  const { dataSystemAll, getDynamicLogger }: any = useRequestStore();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      erpModule: data?.erpModule || undefined,
      systemInformKPI: Array.isArray(data?.systemInformKPI)
        ? data?.systemInformKPI
        : data?.systemInformKPI
        ? [data?.systemInformKPI]
        : [],
      systemInformArea: Array.isArray(data?.systemInformArea)
        ? data?.systemInformArea
        : data?.systemInformArea
        ? [data?.systemInformArea]
        : [],
      systemReceiveData: data?.systemReceiveData || undefined,
      systemCommunicationEffectiveness:
        data?.systemCommunicationEffectiveness || undefined,
    },
  });

  const systemInformKPIOptions = [
    ...formatOptions(systemInformKPIList),
    { value: "Others", label: "Others" },
  ];

  const systemInformAreaOptions = formatOptions(systemInformAreaList);
  const effectivenessOptions = formatOptions(effectivenessList);

  // Watch if "Others" is selected
  const selectedKPIs = watch("systemInformKPI") || [];

  useEffect(() => {
    // Check if "Others" is selected
    const othersSelected = selectedKPIs.includes("Others");
    setHasOtherKPI(othersSelected);

    // Extract custom KPIs from selected values (excluding "Others" and predefined options)
    const predefinedValues = systemInformKPIList.map(
      (item: string | { value: string }) =>
        typeof item === "string" ? item : item.value
    );
    const customValues = selectedKPIs.filter(
      (kpi) => kpi !== "Others" && !predefinedValues.includes(kpi)
    );
    setCustomKPIs(customValues);
  }, [selectedKPIs]);

  // Create options for Select including custom KPIs
  const allKPIOptions = [
    ...systemInformKPIOptions,
    ...customKPIs.map((kpi) => ({ value: kpi, label: kpi })),
  ];

  const handleKPIChange = (selected: any) => {
    const values = selected ? selected.map((opt: any) => opt.value) : [];
    setValue("systemInformKPI", values);
  };

  const addCustomKPI = () => {
    if (customKPI.trim() && !customKPIs.includes(customKPI.trim())) {
      const newCustomKPIs = [...customKPIs, customKPI.trim()];
      setCustomKPIs(newCustomKPIs);

      // Update form value with custom KPIs (excluding "Others" if no custom KPIs)
      const predefinedSelected = selectedKPIs.filter(
        (kpi) => kpi !== "Others" && systemInformKPIList.includes(kpi as any)
      );

      const newValues = [...predefinedSelected, ...newCustomKPIs];
      if (newCustomKPIs.length > 0 && !selectedKPIs.includes("Others")) {
        newValues.push("Others");
      }

      setValue("systemInformKPI", newValues);
      setCustomKPI("");
    }
  };

  const removeCustomKPI = (kpiToRemove: string) => {
    const newCustomKPIs = customKPIs.filter((kpi) => kpi !== kpiToRemove);
    setCustomKPIs(newCustomKPIs);

    // Update form value
    const predefinedSelected = selectedKPIs.filter(
      (kpi) => kpi !== "Others" && systemInformKPIList.includes(kpi as any)
    );

    const newValues = [...predefinedSelected, ...newCustomKPIs];
    if (newCustomKPIs.length > 0) {
      newValues.push("Others");
    }

    setValue("systemInformKPI", newValues);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addCustomKPI();
    }
  };

  const handleCancel = () => {
    reset({
      erpModule: data?.erpModule || undefined,
      systemInformKPI: Array.isArray(data?.systemInformKPI)
        ? data?.systemInformKPI
        : data?.systemInformKPI
        ? [data?.systemInformKPI]
        : [],
      systemInformArea: Array.isArray(data?.systemInformArea)
        ? data?.systemInformArea
        : data?.systemInformArea
        ? [data?.systemInformArea]
        : [],
      systemReceiveData: data?.systemReceiveData || undefined,
      systemCommunicationEffectiveness:
        data?.systemCommunicationEffectiveness || undefined,
    });
    setIsEditing(false);
  };

  const onSubmit = async (itemForm: FormData) => {
    try {
      // If user typed a custom KPI and selected "Others"
      let finalKPIs = [...itemForm.systemInformKPI];
      if (
        finalKPIs.includes("Others") &&
        customKPI.trim() &&
        !finalKPIs.includes(customKPI.trim())
      ) {
        finalKPIs = finalKPIs
          .filter((kpi) => kpi !== "Others")
          .concat(customKPI.trim());
      }

      const payload = {
        datasystems: [
          {
            erpModule: itemForm?.erpModule,
            systemInformKPI: finalKPIs,
            systemInformArea: itemForm.systemInformArea,
            systemReceiveData: itemForm.systemReceiveData,
            systemCommunicationEffectiveness:
              itemForm.systemCommunicationEffectiveness,
            _id: data?._id,
          },
        ],
        email: data?.profile?.user?.email,
        user: data?.profile?.user?._id,
      };

      const res = await axios.put(`/api/v1/data-system`, payload);

      const updatedArray = res?.data?.data;

      const newDocs = dataSystemAll.docs.map((d: any) => {
        const updated = updatedArray.find((u: any) => u._id === d._id);
        if (!updated) return d;
        return {
          ...d,
          ...updated,
          profile:
            typeof updated.profile === "string" ? d.profile : updated.profile,
        };
      });
      const updatedDataSystemAll = { ...dataSystemAll, docs: newDocs };
      getDynamicLogger(updatedDataSystemAll, "dataSystemAll");
      toast.success(
        res?.data?.message ||
          "Data system updated, Changes would appear shortly."
      );

      // toast.success("Data system updated, Changes would appear shortly.");
      setIsEditing(false);
    } catch (error: any) {
      if (error?.response?.data?.error) {
        error.response.data.error.forEach((err: any) =>
          toast.error(err.message || err.msg)
        );
      } else {
        toast.error(error?.response?.data?.message || "Something went wrong");
      }
    }
  };

  return (
    <div className="font-Inter max-w-2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* KPI Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which KPI does this system inform?{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemInformKPI"
            control={control}
            render={({ field }) => (
              <div>
                <Select
                  isMulti
                  options={allKPIOptions}
                  placeholder="Select KPI(s)..."
                  onChange={handleKPIChange}
                  value={allKPIOptions.filter((opt) =>
                    field.value?.includes(opt.value)
                  )}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />

                {/* Custom KPI Input */}
                {hasOtherKPI && (
                  <div className="mt-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customKPI}
                        onChange={(e) => setCustomKPI(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter a custom KPI"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={addCustomKPI}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Press Enter or click Add to include a custom KPI
                    </p>

                    {/* Display custom KPIs */}
                    {customKPIs.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Custom KPIs:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {customKPIs.map((kpi) => (
                            <span
                              key={kpi}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {kpi}
                              <button
                                type="button"
                                onClick={() => removeCustomKPI(kpi)}
                                className="ml-1.5 rounded-full flex-shrink-0"
                              >
                                <span className="sr-only">Remove</span>
                                <svg
                                  className="h-2 w-2"
                                  stroke="currentColor"
                                  fill="none"
                                  viewBox="0 0 8 8"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeWidth="1.5"
                                    d="M1 1l6 6m0-6L1 7"
                                  />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          />
          {errors.systemInformKPI && (
            <p className="text-sm text-red-600">
              {errors.systemInformKPI.message}
            </p>
          )}
        </div>

        {/* Area Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which Area does this system inform?{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemInformArea"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                options={systemInformAreaOptions}
                placeholder="Select Area(s)..."
                onChange={(selected) =>
                  field.onChange(
                    selected ? selected.map((opt: any) => opt.value) : []
                  )
                }
                value={systemInformAreaOptions.filter((opt) =>
                  field.value?.includes(opt.value)
                )}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            )}
          />
          {errors.systemInformArea && (
            <p className="text-sm text-red-600">
              {errors.systemInformArea.message}
            </p>
          )}
        </div>

        {/* External Systems */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which external department and system receives data from this system{" "}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register("systemReceiveData")}
            placeholder="Enter the external system e.g. MWDS Digital Atlas"
            className={`resize-none border w-full p-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.systemReceiveData
                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:border-blue-500"
            }`}
            rows={3}
          />
          {errors.systemReceiveData && (
            <p className="text-sm text-red-600">
              {errors.systemReceiveData.message}
            </p>
          )}
        </div>

        {/* Communication Effectiveness */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How effective is the communication between this system and the one
            that feeds it information? <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemCommunicationEffectiveness"
            control={control}
            render={({ field }) => (
              <Select
                options={effectivenessOptions}
                placeholder="Select Effectiveness..."
                onChange={(selected) => field.onChange(selected?.value || "")}
                value={
                  effectivenessOptions.find(
                    (opt) => opt.value === field.value
                  ) || null
                }
                className="react-select-container"
                classNamePrefix="react-select"
              />
            )}
          />
          {errors.systemCommunicationEffectiveness && (
            <p className="text-sm text-red-600">
              {errors.systemCommunicationEffectiveness.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Go Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-12 py-2.5 bg-[#2070B8] font-medium text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </form>

      <style>{`
        .react-select-container :global(.react-select__control) {
          border-radius: 0.375rem;
          min-height: 42px;
          border-color: #d1d5db;
        }
        .react-select-container :global(.react-select__control--is-focused) {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        .react-select-container
          :global(.react-select__control--is-focused:hover) {
          border-color: #3b82f6;
        }
      `}</style>
    </div>
  );
};

export default ExternalDataSharingEdit;

// import { z } from "zod";
// import toast from "react-hot-toast";
// import { zodResolver } from "@hookform/resolvers/zod";
// import { Controller, useForm } from "react-hook-form";
// import Select from "react-select";
// import {
//   effectivenessList,
//   systemInformAreaList,
//   systemInformKPIList,
// } from "../../../utils/types";
// import { formatOptions } from "../../../utils/helpers";
// import axios from "axios";
// import { useState } from "react";

// const formSchema = z.object({
//   erpModule: z.string().min(1, "Data system is required"),
//   systemInformKPI: z.array(z.string()).min(1, "Required"),
//   systemInformArea: z.array(z.string()).min(1, "Required"),
//   systemReceiveData: z.string().min(1, "Required"),
//   systemCommunicationEffectiveness: z.string().min(1, "Required"),
// });

// type FormData = z.infer<typeof formSchema>;

// const ExternalDataSharingEdit = ({ data, setIsEditing }) => {
//   const [customKPI, setCustomKPI] = useState<string>("");

//   const {
//     register,
//     handleSubmit,
//     reset,
//     control,
//     watch,
//     setValue,
//     formState: { errors, isSubmitting },
//   } = useForm<FormData>({
//     resolver: zodResolver(formSchema),
//     defaultValues: {
//       erpModule: data?.erpModule || undefined,
//       systemInformKPI: Array.isArray(data?.systemInformKPI)
//         ? data?.systemInformKPI
//         : data?.systemInformKPI
//         ? [data?.systemInformKPI]
//         : [],
//       systemInformArea: Array.isArray(data?.systemInformArea)
//         ? data?.systemInformArea
//         : data?.systemInformArea
//         ? [data?.systemInformArea]
//         : [],
//       systemReceiveData: data?.systemReceiveData || undefined,
//       systemCommunicationEffectiveness:
//         data?.systemCommunicationEffectiveness || undefined,
//     },
//   });

//   const systemInformKPIOptions = [
//     ...formatOptions(systemInformKPIList),
//     { value: "Others", label: "Others" },
//   ];

//   const systemInformAreaOptions = formatOptions(systemInformAreaList);
//   const effectivenessOptions = formatOptions(effectivenessList);

//   const handleCancel = () => {
//     reset({
//       erpModule: data?.erpModule || undefined,
//       systemInformKPI: Array.isArray(data?.systemInformKPI)
//         ? data?.systemInformKPI
//         : data?.systemInformKPI
//         ? [data?.systemInformKPI]
//         : [],
//       systemInformArea: Array.isArray(data?.systemInformArea)
//         ? data?.systemInformArea
//         : data?.systemInformArea
//         ? [data?.systemInformArea]
//         : [],
//       systemReceiveData: data?.systemReceiveData || undefined,
//       systemCommunicationEffectiveness:
//         data?.systemCommunicationEffectiveness || undefined,
//     });
//     setIsEditing(false);
//   };

//   const onSubmit = async (itemForm: FormData) => {
//     try {
//       // If user typed a custom KPI and selected "Others"
//       let finalKPIs = [...itemForm.systemInformKPI];
//       if (
//         finalKPIs.includes("Others") &&
//         customKPI.trim() &&
//         !finalKPIs.includes(customKPI.trim())
//       ) {
//         finalKPIs = finalKPIs
//           .filter((kpi) => kpi !== "Others")
//           .concat(customKPI.trim());
//       }

//       const payload = {
//         datasystems: [
//           {
//             erpModule: itemForm?.erpModule,
//             systemInformKPI: finalKPIs,
//             systemInformArea: itemForm.systemInformArea,
//             systemReceiveData: itemForm.systemReceiveData,
//             systemCommunicationEffectiveness:
//               itemForm.systemCommunicationEffectiveness,
//             _id: data?._id,
//           },
//         ],
//         email: data?.profile?.user?.email,
//         user: data?.profile?.user?._id,
//       };

//       const res = await axios.put(`/api/v1/data-system`, payload);

//       // toast.success(res?.data?.message || "Submitted successfully!");
//       toast.success("Data system updated, Changes would appear shortly.");
//       setIsEditing(false);
//     } catch (error: any) {
//       if (error?.response?.data?.error) {
//         error.response.data.error.forEach((err: any) =>
//           toast.error(err.message || err.msg)
//         );
//       } else {
//         toast.error(error?.response?.data?.message || "Something went wrong");
//       }
//     }
//   };

//   // Watch if "Others" is selected
//   const selectedKPIs = watch("systemInformKPI") || [];

//   return (
//     <div className="font-Inter max-w-2xl">
//       <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
//         {/* KPI Selection */}
//         <div className="space-y-2">
//           <label
//             htmlFor="systemInformKPI"
//             className="block text-sm font-medium text-gray-700"
//           >
//             Which KPI does this system inform? *
//           </label>
//           <Controller
//             name="systemInformKPI"
//             control={control}
//             render={({ field }) => (
//               <Select
//                 isMulti
//                 options={systemInformKPIOptions}
//                 placeholder="Select KPI(s)..."
//                 onChange={(selected) =>
//                   field.onChange(selected.map((opt) => opt.value))
//                 }
//                 value={systemInformKPIOptions.filter((opt) =>
//                   field.value?.includes(opt.value)
//                 )}
//               />
//             )}
//           />
//           {/* If "Others" is chosen */}
//           {selectedKPIs.includes("Others") && (
//             <input
//               type="text"
//               value={customKPI}
//               onChange={(e) => setCustomKPI(e.target.value)}
//               placeholder="Enter your KPI"
//               className="mt-2 w-full border p-2 rounded-md"
//             />
//           )}
//           {errors.systemInformKPI && (
//             <p className="text-sm text-red-600">
//               {errors.systemInformKPI.message}
//             </p>
//           )}
//         </div>

//         {/* Area Selection */}
//         <div className="space-y-2">
//           <label
//             htmlFor="systemInformArea"
//             className="block text-sm font-medium text-gray-700"
//           >
//             Which Area does this system inform? *
//           </label>
//           <Controller
//             name="systemInformArea"
//             control={control}
//             render={({ field }) => (
//               <Select
//                 isMulti
//                 options={systemInformAreaOptions}
//                 placeholder="Select Area(s)..."
//                 onChange={(selected) =>
//                   field.onChange(selected.map((opt) => opt.value))
//                 }
//                 value={systemInformAreaOptions.filter((opt) =>
//                   field.value?.includes(opt.value)
//                 )}
//               />
//             )}
//           />
//           {errors.systemInformArea && (
//             <p className="text-sm text-red-600">
//               {errors.systemInformArea.message}
//             </p>
//           )}
//         </div>

//         {/* External Systems */}
//         <div className="space-y-2">
//           <label
//             htmlFor="systemReceiveData"
//             className="block text-sm font-medium text-gray-700"
//           >
//             Which external department and system receives data from this system
//           </label>
//           <textarea
//             id="externalSystems"
//             {...register("systemReceiveData")}
//             placeholder="Enter the external system e.g. MWDS Digital Atlas"
//             className={`resize-none border w-full p-2 rounded-md ${
//               errors.systemReceiveData ? "border-red-500" : ""
//             }`}
//           />
//           {errors.systemReceiveData && (
//             <p className="text-sm text-red-600">
//               {errors.systemReceiveData.message}
//             </p>
//           )}
//         </div>

//         {/* Communication Effectiveness */}
//         <div className="space-y-2">
//           <label
//             htmlFor="systemCommunicationEffectiveness"
//             className="block text-sm font-medium text-gray-700"
//           >
//             How effective is the communication? *
//           </label>
//           <Controller
//             name="systemCommunicationEffectiveness"
//             control={control}
//             render={({ field }) => (
//               <Select
//                 options={effectivenessOptions}
//                 placeholder="Select Effectiveness..."
//                 onChange={(selected) => field.onChange(selected?.value)}
//                 value={
//                   effectivenessOptions.find(
//                     (opt) => opt.value === field.value
//                   ) || null
//                 }
//               />
//             )}
//           />
//           {errors.systemCommunicationEffectiveness && (
//             <p className="text-sm text-red-600">
//               {errors.systemCommunicationEffectiveness.message}
//             </p>
//           )}
//         </div>

//         {/* Action Buttons */}
//         <div className="flex justify-between pt-6">
//           <div className="flex gap-3 pt-6">
//             <button
//               type="button"
//               onClick={handleCancel}
//               className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
//             >
//               Go Back
//             </button>
//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className="px-12 py-2.5 bg-[#2070B8] font-medium text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               {isSubmitting ? "Saving..." : "Save Changes"}
//             </button>
//           </div>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default ExternalDataSharingEdit;
